import React, { useEffect, useRef, useState } from 'react'
import styles from './cart.module.css'
import SvgIcon from '../../components/svgIcon/SvgIcon'
import Text from '../../components/text/Text'
import { useLocation, useNavigate } from 'react-router-dom'
import routes from '../../router/routes'
import { formatCurrency } from '../../utils'
import ModalComponent from '../../components/modal/ModalComponent'
import { labels } from '../../labels'
import OutlinedInput from '../../components/outlinedInput/OutlinedInput'
import OutlinedSelect from '../../components/outlinedSelect/OutlinedSelect'
import { MEASURES } from '../../constants'
import { getInPersonProducts } from '../../services/products'
import ImageDefault from '../../assets/images/image-default.png'

export interface ICartProduct extends IProductObject {
    cant: number
}
interface ICart {
    cart: ICartProduct[]
    onEdit: (product: ICartProduct, cant: number) => void
    onRemove: (product: ICartProduct) => void
}

export function Cart(props: ICart) {
    const scrollRef = useRef<HTMLDivElement>(null)

    const [productSelected, setProductSelected] = useState<
        ICartProduct | undefined
    >(undefined)

    const [showModalDeleteProduct, setShowModalDeleteProduct] = useState(false)
    const [showModalEditProduct, setShowModalEditProduct] = useState(false)

    const [editLocalCant, setEditLocalCant] = useState(0)

    const modalDeleteProduct = () => {
        setShowModalDeleteProduct(false)

        if (productSelected) {
            props.onRemove(productSelected)
            setProductSelected(undefined)
            setEditLocalCant(0)
        }
    }

    const modalEditProduct = () => {
        setShowModalEditProduct(false)

        if (productSelected) {
            props.onEdit(productSelected, editLocalCant)
        }

        setProductSelected(undefined)
        setEditLocalCant(0)
    }

    const selectedProduct = (product: ICartProduct) => {
        setProductSelected(product)
        setEditLocalCant(product.cant)
    }

    const onAddEditProduct = () => {
        setEditLocalCant((prev) => prev + 1)
    }

    const onLessEditProduct = () => {
        if (editLocalCant > 1) {
            setEditLocalCant((prev) => prev - 1)
        } else {
            setEditLocalCant(0)
        }
    }

    const scrollLeft = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -100, behavior: 'smooth' })
        }
    }

    const scrollRight = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 100, behavior: 'smooth' })
        }
    }

    const Item = ({ product }: { product: ICartProduct }) => {
        return (
            <div className={styles.cartItemProduct}>
                <div
                    className={styles.cartItemContent}
                    style={{
                        backgroundImage: `url(${
                            product.product.photoURL || ImageDefault
                        })`,
                    }}
                >
                    <div className={styles.cartItemCant}>
                        <Text
                            label={product.cant.toString()}
                            fontSize={14}
                            color="#464646"
                            fontWeight={600}
                            fontFamily="Montserrat"
                        />
                    </div>
                    <div className={styles.cartItemActions}>
                        <div
                            className={styles.cartActionBtn}
                            style={{ backgroundColor: '#0086d6' }}
                            onClick={() => {
                                selectedProduct(product)
                                setShowModalEditProduct(true)
                            }}
                        >
                            <SvgIcon
                                width={14}
                                height={14}
                                iconName="IconsEditAlt"
                            />
                        </div>
                        <div
                            className={styles.cartActionBtn}
                            style={{ backgroundColor: '#D32F2F' }}
                            onClick={() => {
                                selectedProduct(product)
                                setShowModalDeleteProduct(true)
                            }}
                        >
                            <SvgIcon
                                width={14}
                                height={14}
                                iconName="IconsTrashAlt"
                            />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (props.cart.length === 0) return null

    return (
        <>
            <div className={styles.cartContainer}>
                <div className={styles.cartHeader}>
                    <Text
                        label="Productos añadidos"
                        fontSize={16}
                        fontWeight={400}
                        color="#464646"
                        fontFamily="Montserrat"
                    />
                    <Text
                        label={`${props.cart.reduce(
                            (acc, curr) => acc + curr.cant,
                            0
                        )} productos`}
                        fontFamily="Montserrat"
                        fontSize={14}
                        fontWeight={600}
                        color="#464646"
                    />
                </div>
                <div onClick={scrollLeft} className={styles.arrowCartBtnLeft}>
                    <SvgIcon iconName="ArrowLeftBlue" />
                </div>
                <div onClick={scrollRight} className={styles.arrowCartBtnRight}>
                    <SvgIcon iconName="ArrowLeftBlue" />
                </div>
                <div ref={scrollRef} className={styles.cartCarrusel}>
                    <div className={styles.cartCarruselTrack}>
                        {props.cart.map((item, index) => (
                            <Item key={index} product={item} />
                        ))}
                    </div>
                </div>
            </div>

            <ModalComponent
                show={showModalDeleteProduct}
                modalStyle={styles.modalDeleteProduct}
                handleClose={() => setShowModalDeleteProduct(false)}
            >
                <div className={styles.modalDeleteProductContent}>
                    <div className={styles.modalDeleteProductIcon}>
                        <div className={styles.modalDeleteProductIconCircle1}>
                            <div
                                className={styles.modalDeleteProductIconCircle2}
                            >
                                <SvgIcon
                                    width={20}
                                    height={29}
                                    iconName="IconTrashRed"
                                />
                            </div>
                        </div>
                    </div>
                    <Text
                        label="Eliminar producto"
                        fontFamily="Montserrat"
                        fontSize={20}
                        fontWeight={600}
                        color="#464646"
                        textAlign="center"
                        marginTop={20}
                        marginBottom={10}
                    />
                    <Text
                        label="Estás a punto de eliminar el producto de tu lista. ¿Estás seguro?"
                        fontFamily="Montserrat"
                        fontSize={16}
                        fontWeight={500}
                        color="#6C6C6C"
                        textAlign="center"
                        marginBottom={20}
                    />
                    <ButtonFilled
                        onClick={modalDeleteProduct}
                        text="Eliminar producto"
                    />
                    <ButtonOutline
                        onClick={() => setShowModalDeleteProduct(false)}
                        text="Cancelar"
                    />
                </div>
            </ModalComponent>

            <ModalComponent
                icon={false}
                show={showModalEditProduct}
                modalStyle={styles.modalEditProducts}
                handleClose={() => setShowModalEditProduct(false)}
            >
                <HeaderTitle
                    onBack={() => setShowModalEditProduct(false)}
                    title="Editar producto"
                />

                <div className={styles.modalEditProductsContent}>
                    {productSelected && (
                        <ItemEditProduct
                            product={{
                                ...productSelected,
                                cant: editLocalCant,
                            }}
                            onLess={onLessEditProduct}
                            onAdd={onAddEditProduct}
                        />
                    )}
                    <div className={styles.modalEditProductsBtn}>
                        <ButtonFilled
                            onClick={modalEditProduct}
                            text="Guardar cambios"
                        />
                    </div>
                </div>
            </ModalComponent>
        </>
    )
}

interface IListCartProducts {
    cart: ICartProduct[]
    onAdd: (product: IProductObject) => void
    onLess: (product: IProductObject) => void
}

const LIMIT_PRODUCTS_RESULT = 10

export function ListCartProducts(props: IListCartProducts) {
    const divScroll = useRef(null)

    const [page, setPage] = useState(1)
    const [name, setName] = useState('')

    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)

    const [products, setProducts] = useState<ICartProduct[]>([])

    const syncProductQuantities = (
        baseProducts: ICartProduct[] | IProductObject[]
    ) => {
        return baseProducts.map((item) => {
            const match = props.cart?.find(
                (c) => c.product.id === item.product.id
            )
            return { ...item, cant: match?.cant || 0 }
        })
    }

    useEffect(() => {
        if (products.length > 0) {
            setProducts(() => syncProductQuantities(products))
        }
    }, [props.cart])

    useEffect(() => {
        getProducts(name, page)
    }, [name, page])

    const handleScroll = () => {
        const div = divScroll.current
        if (!div || loading || !hasMore) return

        const { scrollTop, scrollHeight, clientHeight } = div

        if (scrollTop + clientHeight >= scrollHeight - 10) {
            setPage((prev) => prev + 1)
        }
    }

    const getProducts = (name: string, page: number) => {
        setLoading(true)
        getInPersonProducts(page, LIMIT_PRODUCTS_RESULT, name)
            .then((data: IProductsWithPage) => {
                if (data.products.length > 0) {
                    setProducts((old) =>
                        syncProductQuantities([...old, ...data.products])
                    )
                } else {
                    setHasMore(false)
                }
            })
            .catch((error) => console.log(error))
            .finally(() => {
                setLoading(false)
            })
    }

    const onChangeFilter = (name: string) => {
        setProducts([])
        setHasMore(true)
        setPage(1)
        setName(name)
    }

    return (
        <div className={styles.listProductsContainer}>
            <Text
                label="Listado de productos"
                color="#464646"
                fontSize={16}
                fontWeight={400}
                fontFamily="Montserrat"
            />
            <div className={styles.listProductInputContainer}>
                <div className={styles.listProductInputSearchContent}>
                    <div className={styles.listProductInputSearchIcon}>
                        <SvgIcon width={15} height={15} iconName="search" />
                    </div>
                    <input
                        value={name}
                        onChange={(e) => onChangeFilter(e.target.value)}
                        className={styles.listProductInputSearch}
                        placeholder="Busca un producto"
                        autoComplete="off"
                    />
                </div>
            </div>
            <div
                ref={divScroll}
                onScroll={handleScroll}
                className={styles.listProductsContent}
            >
                {products.map((item, index) => (
                    <ItemEditProduct
                        key={index}
                        product={item}
                        onAdd={props.onAdd}
                        onLess={props.onLess}
                    />
                ))}
            </div>
        </div>
    )
}

export function CartScreen() {
    const navigation = useNavigate()
    const location = useLocation()

    const [showModalConfirmProducts, setShowModalConfirmProducts] =
        useState(false)
    // const [showModalProductProvisorio, setShowModalProductProvisorio] =
    //     useState(false)

    const [cart, setCart] = useState<ICartProduct[]>(location.state?.cart || [])

    const handlerToPaymentScreen = () => {
        navigation(`/${routes.payment}`)
    }

    const handleAddProduct = (product: IProductObject) => {
        const productIndex = cart.findIndex(
            (item) => item.product.id === product.product.id
        )
        if (productIndex !== -1) {
            const newCart = [...cart]
            newCart[productIndex].cant += 1
            setCart(newCart)
        } else {
            setCart((old) => [...old, { ...product, cant: 1 }])
        }
    }

    const handleLessProduct = (product: IProductObject) => {
        const productIndex = cart.findIndex(
            (item) => item.product.id === product.product.id
        )
        if (productIndex !== -1) {
            const newCart = [...cart]
            if (newCart[productIndex].cant > 1) {
                newCart[productIndex].cant -= 1
                setCart(newCart)
            } else {
                setCart((old) => [
                    ...old.slice(0, productIndex),
                    ...old.slice(productIndex + 1),
                ])
            }
        }
    }

    const handleEditProduct = (product: ICartProduct, cant: number) => {
        if (cant === 0) {
            handleRemoveProduct(product)
        } else {
            const productIndex = cart.findIndex(
                (item) => item.product.id === product.product.id
            )
            if (productIndex !== -1) {
                const newCart = [...cart]
                newCart[productIndex].cant = cant
                setCart(newCart)
            }
        }
    }

    const handleRemoveProduct = (product: ICartProduct) => {
        const productIndex = cart.findIndex(
            (item) => item.product.id === product.product.id
        )
        if (productIndex !== -1) {
            setCart((old) => [
                ...old.slice(0, productIndex),
                ...old.slice(productIndex + 1),
            ])
        }
    }

    // const addProductsProvisorio = () => {
    //     setUpdateList((old) => old + 1)
    // }

    const handleConfirmProducts = () => {
        setShowModalConfirmProducts(true)
        navigation(`/${routes.payment}`, {
            state: { cart },
        })
    }

    const handleCancelProducts = () => {
        setShowModalConfirmProducts(false)
        navigation(`/${routes.payment}`)
    }

    return (
        <>
            <div className={styles.container}>
                <HeaderTitle
                    onBack={handlerToPaymentScreen}
                    title="Agregar producto a esta venta"
                />

                <div className={styles.space} />

                <div>
                    <Cart
                        cart={cart}
                        onEdit={handleEditProduct}
                        onRemove={handleRemoveProduct}
                    />
                </div>

                <div>
                    <ListCartProducts
                        cart={cart}
                        onAdd={handleAddProduct}
                        onLess={handleLessProduct}
                    />
                </div>

                <div className={styles.cartActionContainer}>
                    {/* <ButtonOutline
                        onClick={() => setShowModalProductProvisorio(true)}
                        text="Agregar producto “provisorio”"
                    /> */}
                    <ButtonFilled
                        disabled={cart.length === 0}
                        onClick={() => setShowModalConfirmProducts(true)}
                        text="Confirmar productos"
                    />
                </div>
            </div>

            <ModalComponent
                icon={false}
                show={showModalConfirmProducts}
                modalStyle={styles.modalEditProducts}
                handleClose={() => setShowModalConfirmProducts(false)}
            >
                <HeaderTitle
                    onBack={() => setShowModalConfirmProducts(false)}
                    title="Resultados de compra"
                />

                <div className={styles.modalConfirmProductsContainer}>
                    <div className={styles.modalConfirmProductsContent}>
                        {cart.map((item, index) => (
                            <ItemEditProduct
                                key={index}
                                product={item}
                                noEdit={true}
                                onLess={() => console.log}
                                onAdd={() => console.log}
                            />
                        ))}
                    </div>
                    <div className={styles.modalConfirmProductsActionContent}>
                        <ButtonFilled
                            onClick={handleConfirmProducts}
                            text="Cobrar venta"
                            bg="#74BE00"
                        />
                        <div className={styles.modalConfirmProductsActionGroup}>
                            <div
                                className={styles.modalConfirmProductsActionBtn}
                            >
                                <ButtonFilled
                                    onClick={() =>
                                        setShowModalConfirmProducts(false)
                                    }
                                    text="Agregar productos"
                                />
                            </div>
                            <div
                                className={styles.modalConfirmProductsActionBtn}
                            >
                                <ButtonOutline
                                    onClick={handleCancelProducts}
                                    text="Cancelar venta"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </ModalComponent>

            {/* <ModalAddProductProvisorio
                onSave={addProductsProvisorio}
                show={showModalProductProvisorio}
                onClose={() => setShowModalProductProvisorio(false)}
            /> */}
        </>
    )
}

interface IHeaderTitle {
    onBack: () => void
    title: string
}

function HeaderTitle(props: IHeaderTitle) {
    return (
        <div className={styles.header}>
            <div className={styles.headerIcon} onClick={props.onBack}>
                <SvgIcon iconName="IconArrowLeft" />
            </div>
            <div className={styles.headerTitle}>
                <Text
                    label={props.title}
                    color="#ffffff"
                    fontFamily="Montserrat"
                    fontSize={16}
                    fontWeight={600}
                />
            </div>
        </div>
    )
}

interface IItemEditProduct {
    noEdit?: boolean
    onAdd: (product: IProductObject) => void
    onLess: (product: IProductObject) => void
    product: ICartProduct
}

function ItemEditProduct(props: IItemEditProduct) {
    const getPercentage = (origin?: number, _new?: number) => {
        if (origin && _new) {
            const discount = ((origin - _new) / origin) * 100
            return Math.round(discount)
        }

        return 0
    }
    return (
        <div className={styles.listProductsItemContent}>
            <div className={styles.listProductsItemImageContent}>
                <img
                    src={props.product.product.photoURL || ImageDefault}
                    alt="producto"
                />
            </div>
            <div className={styles.listProductsItemTextContent}>
                <Text
                    label={props.product.product.productName}
                    color="#565656"
                    fontFamily="Montserrat"
                    fontSize={16}
                    fontWeight={600}
                />
                <Text
                    label={`UM: ${props.product.product.weight || ''}${
                        props.product.product.productMeasureUnit || ''
                    }`}
                    color="#565656"
                    fontFamily="Montserrat"
                    fontSize={16}
                    fontWeight={400}
                />
                <div className={styles.listProductsItemTextContentPrice}>
                    <Text
                        label={formatCurrency(
                            props.product.productPrices[0].saleAmount
                        )}
                        color="#565656"
                        fontFamily="Montserrat"
                        fontSize={16}
                        fontWeight={600}
                    />
                    {props.product.product.settlement && (
                        <>
                            <Text
                                label={formatCurrency(
                                    props.product.productPrices[0].priceOriginal
                                )}
                                color="#A4A4A4"
                                fontFamily="Montserrat"
                                fontSize={16}
                                fontWeight={500}
                                textDecorationLine="line-through"
                            />
                            <div
                                className={
                                    styles.listProductsItemTextContentPriceDiscount
                                }
                            >
                                <Text
                                    label={`${getPercentage(
                                        props.product.productPrices[0]
                                            .priceOriginal,
                                        props.product.productPrices[0]
                                            .saleAmount
                                    )}%`}
                                    fontFamily="Montserrat"
                                    fontSize={14}
                                    fontWeight={600}
                                    color="#FFF"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className={styles.listProductsItemActionsContent}>
                {props.noEdit ? (
                    <Text
                        label={`Cantidad: ${props.product.cant}`}
                        color="#565656"
                        fontWeight={600}
                        fontSize={16}
                        fontFamily="Montserrat"
                    />
                ) : (
                    <div className={styles.listProductsItemActionsGroup}>
                        <div
                            className={styles.listProductsItemActionsBtnLess}
                            onClick={() => props.onLess(props.product)}
                        >
                            <Text label="-" fontSize={25} fontWeight={400} />
                        </div>
                        <Text
                            label={props.product.cant.toString()}
                            fontFamily="Montserrat"
                            fontSize={14}
                            fontWeight={600}
                            color="#565656"
                        />
                        <div
                            className={styles.listProductsItemActionsBtnPlus}
                            onClick={() => props.onAdd(props.product)}
                        >
                            <Text
                                label="+"
                                fontSize={20}
                                fontWeight={400}
                                color="#fff"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

interface IButton {
    onClick: () => void
    disabled?: boolean
    text: string
    bg?: string
}

function ButtonFilled(props: IButton) {
    return (
        <div
            className={styles.cartBtnFilled}
            onClick={() => (props.disabled ? undefined : props.onClick())}
            style={{ backgroundColor: props.disabled ? '#eee' : props.bg }}
        >
            <Text
                label={props.text}
                fontFamily="Montserrat"
                fontSize={16}
                fontWeight={600}
                color={props.disabled ? '#757575' : '#ffffff'}
            />
        </div>
    )
}

function ButtonOutline(props: IButton) {
    return (
        <div className={styles.cartBtnOutline} onClick={props.onClick}>
            <Text
                label={props.text}
                fontFamily="Montserrat"
                fontSize={16}
                fontWeight={600}
                color="#0086D6"
            />
        </div>
    )
}

interface IModalAddProductProvisorio {
    show: boolean
    onSave: () => void
    onClose: () => void
}

export function ModalAddProductProvisorio(props: IModalAddProductProvisorio) {
    const [form, setForm] = useState({
        productName: '',
        productEan: '',
        unit: '',
        weight: '',
        price: '',
        stock: '',
    })

    const [errors, setErrrors] = useState<
        | {
              productName?: ''
              productEan?: ''
              unit?: ''
              weight?: ''
              price?: ''
              stock?: ''
          }
        | undefined
    >()

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        console.log(name, value)
        setForm((prevState) => ({
            ...prevState,
            [name]: value,
        }))
    }

    const handlerSaveProduct = () => {
        setErrrors(undefined)
        console.log(form)
        props.onSave()
        props.onClose()
    }

    return (
        <ModalComponent
            icon={true}
            show={props.show}
            handleClose={props.onClose}
            modalStyle={styles.modalProductProvisorio}
        >
            <div className={styles.modalProvisorioContainer}>
                <div className={styles.modalProvisorioTitleWrapper}>
                    <Text
                        label={labels.productInformation}
                        variant={styles.modalProvisorioTextWidth}
                        fontWeight={600}
                        color="#184287"
                        fontSize={16}
                    />
                    <div className={styles.modalProvisorioDivider} />
                </div>
                <div className={styles.modalProvisorioTextWrapper}>
                    <div>
                        <SvgIcon iconName="IconInfoBlue" />
                    </div>
                    <Text
                        fontSize={14}
                        color="#1E1E1E"
                        fontWeight={400}
                        label="Puedes completar la información de tu producto luego de crearlo como “Producto provisorio” en “Mis productos”"
                    />
                </div>
                <div className={styles.modalProvisorioInputContainer}>
                    <OutlinedInput
                        floatingLabel={labels.name}
                        onChange={handleChange}
                        value={form.productName}
                        placeholder={labels.productNamePlaceholder}
                        minLength={0}
                        error={errors?.productName}
                        maxLength={42}
                        charCounter
                        min="0"
                        max="42"
                    />
                </div>
                <div className={styles.modalProvisorioInputContainer}>
                    <OutlinedInput
                        floatingLabel="EAN"
                        onChange={handleChange}
                        value={form.productEan}
                        placeholder="0000000000000"
                        minLength={0}
                        error={errors?.productEan}
                        maxLength={42}
                        min="0"
                        max="42"
                    />
                </div>
                <div className={styles.modalProvisorioRow}>
                    <div className={styles.modalProvisorioRowItem1}>
                        <OutlinedSelect
                            floatingLabel={labels.unit}
                            options={MEASURES}
                            // error={errors?.productMeasureUnit}
                            // onChange={handleChange}
                        />
                    </div>
                    <div className={styles.modalProvisorioRowItem2}>
                        <OutlinedInput
                            floatingLabel={labels.quantity}
                            type="text"
                            onChange={handleChange}
                            value={form.weight}
                            // isNumber={unit !== 'Lt' && unit !== 'Kg'}
                            // isDecimal={unit === 'Lt' || unit === 'Kg'}
                            min="0"
                            placeholder={labels.quantityPlaceholder}
                            error={errors?.weight}
                        />
                    </div>
                </div>
                <div className={styles.modalProvisorioRow}>
                    <div className={styles.modalProvisorioRowItem1}>
                        <OutlinedInput
                            onChange={handleChange}
                            value={form.price}
                            floatingLabel={labels.price}
                            isNumber
                            placeholder={labels.normalPlaceholder}
                            error={errors?.price}
                        />
                    </div>
                    <div className={styles.modalProvisorioRowItem2}>
                        <OutlinedInput
                            floatingLabel={labels.stock}
                            onChange={handleChange}
                            value={form.stock}
                            isNumber
                            placeholder={labels.quantityPlaceholder}
                            error={errors?.stock}
                        />
                    </div>
                </div>
                <div className={styles.modalProvisorioBtnContainer}>
                    <ButtonFilled
                        onClick={handlerSaveProduct}
                        text="Agregar producto a venta"
                    />
                </div>
            </div>
        </ModalComponent>
    )
}
